import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { listingApiServerFetchOrg } from '#/modules/listing/orgs/api/listingApiFetchOrg';
import { viewOrgReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { DynamicSearchOrgsRefinements } from '#/modules/search/orgs/components/Refinements/DynamicSearchOrgsRefinements';
import { userDashboardApiServerFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { idealistRedirect } from '#/routing/utils/idealistRedirect';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewOrgPage } from './ViewOrgPage';

export const handle = {
  searchRefinements: <DynamicSearchOrgsRefinements />,
} satisfies LayoutHandleElements;

const DynamicUnpublishedOrgPage = dynamic(
  () =>
    import('#/modules/listing/orgs/pages/unpublished/UnpublishedOrgPage').then(
      (m) => ({ default: m.UnpublishedOrgPage }),
    ),
  { strategy: 'always' },
);

export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<typeof viewOrgReferenceRoute>) {
  return currentUrl.pathname !== nextUrl.pathname;
}

export async function loader({
  request,
  url,
  context,
  params: { orgIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewOrgReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const sharedHandle = {
    defaultSearchType: 'ORG',
    showSearchRefinements: true,
    showDesktopSearch: true,
    hideMobileHeaderOnScroll: true,
  } satisfies LayoutHandleProps;

  const [orgId] = orgIdDashSlug.split('-');

  try {
    const [org, listingExtras] = await Promise.all([
      listingApiServerFetchOrg({ request, context })(orgId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiServerFetchListingExtras({
            request,
            context,
          })([orgId])
        : null,
    ]);

    if (!org) {
      return idealistNotFound({
        type: 'NOT_FOUND',
        handle: sharedHandle,
      } as const);
    }

    if (org.type === 'REDIRECT') {
      return idealistRedirect({
        context,
        fromUrl: url,
        to: org.url,
        status: 301,
      });
    }

    if (org.type === 'UNPUBLISHED_ORG') {
      return idealistNotFound({
        type: 'UNPUBLISHED_ORG',
        unpublishedOrg: org,
        handle: {
          ...sharedHandle,
          hideFooterDivider: 'hide-footer-divider-if-logged-out',
        } satisfies LayoutHandleProps,
      } as const);
    }

    return {
      type: 'ORG',
      org,
      saved: Boolean(listingExtras?.[org.id]?.saved),
      locale: serverContext.locale,
      handle: {
        ...sharedHandle,
        hideFooterDivider: 'hide-footer-divider-if-logged-out',
        searchFromPageType: 'LISTING',
        urlByLocale: org.url,
      } satisfies LayoutHandleProps,
      now: serverContext.now,
    } as const;
  } catch {
    return idealistNotFound({
      type: 'NOT_FOUND',
      handle: sharedHandle,
    } as const);
  }
}

export default function ViewHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <DynamicNotFoundPage />;
    case 'ORG':
      return (
        <RouteModule
          title={`${loaderData.org.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['ORG']}`}
          ogImage={imageUrlForOgMeta({
            data: loaderData.org.logo,
          })}
          description={loaderData.org.descriptionTruncated?.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.org.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.org.url[loaderData.org.locale],
          }}
          schemaMarkups={[
            loaderData.org.schemaOrgMarkup?.[loaderData.locale],
            loaderData.org.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <ViewOrgPage
            initialOrg={loaderData.org}
            initiallySaved={loaderData.saved}
            previewOrg={null}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_ORG':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <DynamicUnpublishedOrgPage
            unpublishedOrg={loaderData.unpublishedOrg}
          />
        </RouteModule>
      );
  }
}
